import React, { useState } from 'react';
import { Facebook, Mail, Phone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import SendMessageModal from '@/components/SendMessageModal';

const Footer = () => {
  const { user } = useAuth();
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showSendMessageModal, setShowSendMessageModal] = useState(false);

  const handleLinkClick = (modalSetter) => (e) => {
    e.preventDefault();
    modalSetter(true);
  };

  return (
    <>
      <footer className="bg-gradient-to-r from-blue-900 to-purple-900 text-white mt-auto">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* About Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://radianceedu.com/wp-content/uploads/2025/11/Logo-Radiance-Education.png" 
                  alt="Radiance Education" 
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-sm text-blue-100 leading-relaxed">
                Radiance Education is committed to providing quality IELTS preparation 
                and exam services. Book your exam slots easily and prepare for success.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-blue-100">
                <li>
                  <a 
                    href="https://radianceedu.com/about-radiance" 
                    onClick={handleLinkClick(setShowAboutModal)}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a 
                    href="https://radianceedu.com/exam-results" 
                    onClick={handleLinkClick(setShowResultsModal)}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Exam Results
                  </a>
                </li>
                <li>
                  <a 
                    href="https://radianceedu.com/privacy-policy" 
                    onClick={handleLinkClick(setShowPrivacyModal)}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a 
                    href="https://radianceedu.com/exam-booking-conditions" 
                    onClick={handleLinkClick(setShowConditionsModal)}
                    className="hover:text-white transition-colors cursor-pointer"
                  >
                    Exam Booking Conditions
                  </a>
                </li>
                {/* Only show for logged in students - Highlighted */}
                {user && user.role === 'student' && (
                  <li>
                    <a 
                      href="https://csbpathway.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-semibold rounded-full hover:from-yellow-300 hover:to-orange-400 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-105"
                    >
                      <span>🌍</span>
                      Find Your Study Destination
                      <span className="text-xs">→</span>
                    </a>
                  </li>
                )}
              </ul>
            </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-bold mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm text-blue-100">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <a href="tel:+8801759787364" className="hover:text-white transition-colors">
                  01759787364
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href="mailto:info@radianceedu.com" className="hover:text-white transition-colors">
                  info@radianceedu.com
                </a>
              </li>
              <li className="flex items-center gap-2 mt-4">
                <a 
                  href="https://www.facebook.com/radianceedumb" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors flex items-center gap-2"
                >
                  <Facebook className="w-5 h-5" />
                  <span>Visit Facebook Page</span>
                </a>
              </li>
              {user && user.role === 'student' && (
                <li className="mt-4">
                  <Button
                    onClick={() => setShowSendMessageModal(true)}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold"
                    size="sm"
                  >
                    💬 অভিযোগ ও পরামর্শ
                  </Button>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/20 mt-8 pt-6 text-center text-sm text-blue-100">
          <p>© {new Date().getFullYear()} Radiance Education. All rights reserved. Development Credit: <a href="https://virtualswift.com/" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300">VirtualSwift</a></p>
        </div>
      </div>
    </footer>

    {/* About Us Modal */}
    <Dialog open={showAboutModal} onOpenChange={setShowAboutModal}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <DialogHeader>
          <DialogTitle className="text-2xl md:text-3xl font-bold text-center bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent px-2">
            About Radiance Education
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto h-[78vh] px-2 md:px-4 py-2">
          {/* Vision Section */}
          <div className="mb-6 md:mb-8 bg-white rounded-2xl shadow-lg p-4 md:p-6">
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xl md:text-2xl">⚡</span>
              </div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-800">A Vision for Quality — Alhamdulillah, Now It's a Proven Reality</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4 text-base md:text-lg">
              Radiance Education is a dedicated IELTS and English training institute focused on real results. You can also explore our website or Facebook page to see student feedback and results for a clear idea of what you can achieve with us. Radiance Education supports you with a structured roadmap and a caring learning environment.
            </p>
            <div className="grid md:grid-cols-2 gap-4 md:gap-6 mt-4 md:mt-6">
              <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl p-3 md:p-4 text-center">
                <div className="text-3xl md:text-4xl font-bold text-blue-600">3K+</div>
                <div className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Students completed courses</div>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl p-3 md:p-4 text-center">
                <div className="text-3xl md:text-4xl font-bold text-purple-600">3.8K+</div>
                <div className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Students enrolled in different courses</div>
              </div>
            </div>
          </div>

          {/* 2025 British Council Award - 2nd Consecutive Year */}
          <div className="mb-6 md:mb-8 bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 rounded-2xl shadow-2xl p-1">
            <div className="bg-white rounded-xl p-4 md:p-6">
              <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse">
                  <span className="text-xl md:text-2xl">🏆</span>
                </div>
                <div>
                  <h2 className="text-lg md:text-2xl font-bold text-gray-800">British Council Top Performer (Top 5) Sylhet Award - 2025</h2>
                  <p className="text-sm md:text-base text-emerald-600 font-semibold">🎉 2nd Consecutive Year Achievement!</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                {/* Trophy Image */}
                <div className="group">
                  <div className="relative overflow-hidden rounded-xl shadow-lg transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl">
                    <img 
                      src="/footer-image-1.jpg"
                      alt="British Council Top 5 Award Trophy 2025"
                      className="w-full h-auto object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>

                {/* Team Receiving Award Image */}
                <div className="group">
                  <div className="relative overflow-hidden rounded-xl shadow-lg transform transition-all duration-300 group-hover:scale-105 group-hover:shadow-2xl">
                    <img 
                      src="/footer-image-2.jpg"
                      alt="Receiving British Council Top 5 Award 2025"
                      className="w-full h-auto object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 md:p-4 border-l-4 border-emerald-500 mb-4">
                <p className="text-gray-700 text-xs md:text-sm italic leading-relaxed">
                  "Alhamdulillah! For the 2nd consecutive year, Radiance Education has been honored with the British Council Top Performer (Top 5) Sylhet Award on December 15, 2025. This recognition reflects our unwavering commitment to excellence, student success, and maintaining the highest standards in IELTS preparation. We're grateful to our dedicated team, supportive students, and the community that makes this achievement possible."
                </p>
              </div>
            </div>
          </div>

          {/* 2024 British Council Award - First Achievement */}
          <div className="mb-6 md:mb-8 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl shadow-2xl p-1">
            <div className="bg-white rounded-xl p-4 md:p-6">
              <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl md:text-2xl">🏆</span>
                </div>
                <h2 className="text-lg md:text-2xl font-bold text-gray-800">British Council Top Performer Sylhet Award - 2024</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                {/* Award Receiving Image */}
                <div className="group">
                  <div className="relative overflow-hidden rounded-xl shadow-lg transform transition-all duration-300 group-hover:scale-105">
                    <img 
                      src="https://radianceedu.com/wp-content/uploads/2025/07/7581.jpg"
                      alt="British Council Award Receiving"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>

                {/* Award Certificate Image */}
                <div className="group">
                  <div className="relative overflow-hidden rounded-xl shadow-lg transform transition-all duration-300 group-hover:scale-105">
                    <img 
                      src="https://radianceedu.com/wp-content/uploads/2025/07/481795034_1340858193900611_8455544747844418039_n-1.jpg"
                      alt="British Council Award Certificate"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-3 md:p-4 border-l-4 border-orange-500">
                <p className="text-gray-700 text-xs md:text-sm italic">
                  "In just 1.5 years of our journey, Alhamdulillah, we proudly achieved the British Council Top Performer Sylhet Award within our very first year — a milestone that speaks volumes about our dedication, quality, and student-first approach."
                </p>
              </div>
            </div>
          </div>

          {/* Founders Section */}
          <div className="mb-6 md:mb-8 bg-white rounded-2xl shadow-lg p-4 md:p-6">
            <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xl md:text-2xl">👥</span>
              </div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-800">Meet Our Founders</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 md:gap-8">
              {/* Founder 1 */}
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-xl transform transition-all duration-300 group-hover:scale-105">
                  <img 
                    src="https://radianceedu.com/wp-content/uploads/2025/08/Untitled-design-3-1.png"
                    alt="Yasir Rahman - Co-Founder"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6">
                    <h3 className="text-xl font-bold text-white mb-1">Yasir Rahman</h3>
                    <p className="text-blue-200 text-sm">Co-founder, CEO & Senior IELTS Instructor</p>
                  </div>
                </div>
              </div>

              {/* Founder 2 */}
              <div className="group">
                <div className="relative overflow-hidden rounded-2xl shadow-xl transform transition-all duration-300 group-hover:scale-105">
                  <img 
                    src="https://radianceedu.com/wp-content/uploads/2025/08/Untitled-design-1.png"
                    alt="Mustakim Ahmed - Co-Founder"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6">
                    <h3 className="text-xl font-bold text-white mb-1">Mustakim Ahmed</h3>
                    <p className="text-purple-200 text-sm">Co-founder & Managing Director</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 1st Anniversary Section */}
          <div className="mb-6 md:mb-8 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 rounded-2xl shadow-2xl p-1">
            <div className="bg-white rounded-xl p-4 md:p-6">
              <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center animate-pulse flex-shrink-0">
                  <span className="text-xl md:text-2xl">🎉</span>
                </div>
                <h2 className="text-lg md:text-2xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                  1st Anniversary Celebration & Raffle Draw Memories
                </h2>
              </div>
              
              <p className="text-gray-700 leading-relaxed mb-4 md:mb-6 text-sm md:text-base">
                <strong>Alhamdulillah!</strong> 🎊 We are deeply thankful to everyone who joined us in celebrating Radiance Education's 
                1st Anniversary & Mega Offer Raffle Draw 2024.
                <br /><br />
                To our beloved students, respected guests — especially the <strong>Team from the British Council</strong> — and all 
                well-wishers, your presence made this milestone truly unforgettable.
                <br /><br />
                Your prayers, encouragement, and unwavering support have been the foundation of everything we've achieved so far.
              </p>

              {/* YouTube Video Embed - HD Quality */}
              <div className="relative w-full rounded-xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/tLNldW0FwJI?vq=hd1080&hd=1&quality=hd1080&rel=0&modestbranding=1"
                  title="Radiance Education 1st Anniversary Celebration"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>

              <div className="mt-4 md:mt-6 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl p-3 md:p-4 border-l-4 border-purple-500">
                <p className="text-gray-700 italic text-sm md:text-base">
                  "Together, we continue to build a brighter future for every student. Thank you for being part of the Radiance family!"
                </p>
              </div>
            </div>
          </div>

          {/* Footer Section */}
          <div className="text-center mt-6 md:mt-8 pb-4">
            <div className="inline-block bg-white rounded-full px-6 md:px-8 py-3 shadow-lg">
              <p className="text-gray-600 font-semibold text-sm md:text-base">Copyright © 2024-2026 Radiance Education. All Rights Reserved.</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Exam Booking Conditions Modal */}
    <Dialog open={showConditionsModal} onOpenChange={setShowConditionsModal}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-red-700">
            Exam Booking Conditions
          </DialogTitle>
          <p className="text-sm text-gray-500 text-center">Last Updated: December 30, 2025</p>
        </DialogHeader>
        <div className="overflow-y-auto h-[75vh] px-6 py-4 prose prose-sm max-w-none">
          <div className="space-y-6">
            {/* Important Notice */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <h3 className="text-lg font-bold text-red-800 mb-2">⚠️ Important Notice</h3>
              <p className="text-gray-800">
                By booking any exam (Mock Test or Partial Exam) through the Radiance Education portal, you acknowledge that you have read, understood, and agree to comply with all conditions mentioned below.
              </p>
            </div>

            {/* General Booking Terms */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">1. General Booking Terms</h3>
              
              <h4 className="font-bold text-red-600 mt-3 mb-2">1.1 Eligibility</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Only registered students of Radiance Education can book exams</li>
                <li>Your portal access must be active and valid</li>
                <li>You must have sufficient credits in your account</li>
                <li>Your account must not be suspended or on hold</li>
              </ul>

              <h4 className="font-bold text-red-600 mt-3 mb-2">1.2 Portal Validity</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Portal access and exam booking facility is valid for <strong>5 months from your class start date</strong></li>
                <li>All exams must be booked and completed within this validity period</li>
                <li>Extensions may be granted only under exceptional circumstances with written approval from management</li>
              </ul>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 mt-3 rounded">
                <p className="text-gray-800 font-semibold">
                  ⚡ Radiance Education authority reserves the right to modify, add, or remove any booking rules at any time without prior notice.
                </p>
              </div>
            </section>

            {/* Credit System */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">2. Credit System</h3>
              
              <h4 className="font-bold text-red-600 mt-3 mb-2">2.1 Credit Allocation</h4>
              <p className="text-gray-700 mb-2">Credits are allocated to students upon enrollment or purchase. Different modules require different credit amounts:</p>
              <ul className="list-disc pl-5 text-gray-700">
                <li><strong>Listening:</strong> 1 credit per exam</li>
                <li><strong>Reading:</strong> 1 credit per exam</li>
                <li><strong>Writing:</strong> 1 credit per exam</li>
                <li><strong>Speaking:</strong> 1 credit per exam</li>
                <li><strong>Mock Test (Full):</strong> 1 credit (includes L+R+W+Speaking)</li>
              </ul>

              <h4 className="font-bold text-red-600 mt-3 mb-2">2.2 Credit Validity</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Credits are valid only within your portal validity period (5 months)</li>
                <li>Unused credits expire after the validity period</li>
                <li>Credits are non-transferable and non-refundable</li>
              </ul>

              <h4 className="font-bold text-red-600 mt-3 mb-2">2.3 Credit History</h4>
              <div className="bg-purple-50 border-l-4 border-purple-500 p-3 rounded">
                <p className="text-gray-800 mb-2">
                  <strong>Effective from December 30, 2025</strong>, the <strong>"Credit History"</strong> feature is now available in your portal.
                </p>
                <p className="text-gray-700 mb-2">This feature displays:</p>
                <ul className="list-disc pl-5 text-gray-700">
                  <li>Complete record of credit deductions for Exam Bookings</li>
                  <li>Records of credit additions/deductions made by the Institute with reasons</li>
                  <li>Date and time of each transaction</li>
                  <li>Current balance for each module</li>
                </ul>
                <p className="text-gray-600 mt-2 text-sm italic">
                  📌 Note: Transactions made before December 30, 2025 will not appear in the Credit History.
                </p>
              </div>
            </section>

            {/* Booking Rules */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">3. Booking Rules</h3>
              
              <h4 className="font-bold text-red-600 mt-3 mb-2">3.1 Advance Booking</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li><strong>Partial Exams:</strong> Can be booked up to 30 days in advance</li>
                <li><strong>Mock Tests:</strong> Can be booked up to 60 days in advance</li>
                <li><strong>Speaking Tests:</strong> Must be booked separately after LRW booking</li>
                <li>Bookings are confirmed on a first-come, first-served basis</li>
              </ul>

              <h4 className="font-bold text-red-600 mt-3 mb-2">3.2 Booking Limits</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Maximum <strong>2 active bookings</strong> per module at any time</li>
                <li>You cannot book the same exam date twice</li>
                <li>Speaking slots are limited and subject to availability</li>
              </ul>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-3 mt-3 rounded">
                <p className="text-gray-800">
                  <strong>📧 Booking Confirmation:</strong> You will receive email confirmation upon successful booking. Check your email regularly for updates.
                </p>
              </div>
            </section>

            {/* Cancellation Policy */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">4. Cancellation Policy</h3>
              
              <h4 className="font-bold text-red-600 mt-3 mb-2">4.1 Student-Initiated Cancellation</h4>
              
              <div className="bg-blue-50 p-3 rounded mb-2">
                <p className="font-bold text-blue-800 mb-1">Cancellation Window:</p>
                <p className="text-gray-700">Cancellations must be made <strong>at least 3 days (72 hours) before the exam date</strong>. Cancellations within 3 days of the exam are NOT allowed.</p>
              </div>

              <div className="bg-green-50 p-3 rounded mb-2">
                <p className="font-bold text-green-800 mb-1">Monthly Limit:</p>
                <p className="text-gray-700">Students can cancel a maximum of <strong>2 exams per month</strong> (both partial and mock combined). This limit resets on the 1st day of each month.</p>
              </div>

              <div className="bg-purple-50 p-3 rounded">
                <p className="font-bold text-purple-800 mb-1">Credit Refund:</p>
                <ul className="list-disc pl-5 text-gray-700">
                  <li>Full credit refund if cancelled within the allowed timeframe</li>
                  <li>No refund for late cancellations or no-shows</li>
                </ul>
              </div>

              <h4 className="font-bold text-red-600 mt-3 mb-2">4.2 Institute-Initiated Cancellation</h4>
              <p className="text-gray-700 mb-2">Radiance Education may cancel your booking if:</p>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Insufficient number of students enrolled for that slot</li>
                <li>Exam venue is unavailable due to unforeseen circumstances</li>
                <li>Natural disasters, public holidays, or government orders</li>
              </ul>
              <p className="text-green-700 font-semibold mt-2">✅ In case of institute cancellation, full credit refund will be provided immediately.</p>
            </section>

            {/* No-Show Policy */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">5. No-Show Policy (Critical)</h3>
              
              <div className="bg-red-100 border-2 border-red-500 p-4 rounded">
                <h4 className="font-bold text-red-700 mb-2">⚠️ Definition of No-Show</h4>
                <p className="text-gray-800 mb-2">A "no-show" occurs when you:</p>
                <ul className="list-disc pl-5 text-gray-800">
                  <li>Book an exam but do not appear at the venue</li>
                  <li>Arrive more than 15 minutes late after exam start time</li>
                  <li>Do not bring required identification documents</li>
                  <li>Are found to be in violation of exam rules</li>
                </ul>
              </div>

              <div className="bg-red-50 p-4 rounded mt-3">
                <h4 className="font-bold text-red-700 mb-3">🚨 No-Show Penalties</h4>
                
                <div className="space-y-3">
                  <div className="border-l-4 border-orange-500 pl-3">
                    <p className="font-bold text-orange-700">First Offense:</p>
                    <ul className="list-disc pl-5 text-gray-700">
                      <li>The credit used for booking will <strong>NOT be refunded</strong></li>
                      <li><strong>Additional penalty:</strong> Deduction of 1 extra credit from your account</li>
                      <li>Written warning issued</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-red-500 pl-3">
                    <p className="font-bold text-red-700">Second Offense:</p>
                    <ul className="list-disc pl-5 text-gray-700">
                      <li>Credit for booking forfeited</li>
                      <li><strong>Penalty:</strong> Deduction of 2 extra credits</li>
                      <li>Temporary suspension from booking for 7 days</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-red-700 pl-3">
                    <p className="font-bold text-red-800">Third Offense:</p>
                    <ul className="list-disc pl-5 text-gray-700">
                      <li>Credit for booking forfeited</li>
                      <li><strong>Penalty:</strong> Deduction of 3 extra credits</li>
                      <li>Booking privileges suspended for 15 days</li>
                      <li>Meeting with management required before reinstatement</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-red-900 pl-3">
                    <p className="font-bold text-red-900">Repeated Offenses:</p>
                    <ul className="list-disc pl-5 text-gray-700">
                      <li>May result in permanent suspension of portal access</li>
                      <li>Possible termination of enrollment</li>
                      <li>No refund of any fees paid</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-3 rounded mt-3">
                <h4 className="font-bold text-green-700 mb-2">✅ No-Show Exceptions</h4>
                <p className="text-gray-700 mb-2">Penalties may be waived ONLY in case of:</p>
                <ul className="list-disc pl-5 text-gray-700">
                  <li>Medical emergency (with valid doctor's certificate)</li>
                  <li>Immediate family emergency (with proof)</li>
                  <li>Natural disasters or force majeure events</li>
                </ul>
                <p className="text-gray-800 font-semibold mt-2">
                  📞 Required Action: Notify the institute immediately and submit supporting documents within 48 hours.
                </p>
              </div>
            </section>

            {/* Exam Day Rules */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">6. Exam Day Rules</h3>
              
              <h4 className="font-bold text-red-600 mt-3 mb-2">6.1 Arrival Time</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Arrive <strong>at least 15 minutes before</strong> the scheduled exam time</li>
                <li>Late arrivals (after start time) will not be permitted entry</li>
                <li>No exceptions for traffic or personal delays</li>
              </ul>

              <h4 className="font-bold text-red-600 mt-3 mb-2">6.2 Required Items</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li><strong>Admit card</strong> (printed or digital)</li>
                <li><strong>Original photo ID</strong> (National ID Card or Passport)</li>
                <li><strong>Stationery:</strong> Pen, pencil, eraser</li>
              </ul>

              <h4 className="font-bold text-red-600 mt-3 mb-2">6.3 Prohibited Items</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Mobile phones, smartwatches, electronic devices</li>
                <li>Books, notes, or study materials</li>
                <li>Calculators (unless specifically allowed)</li>
              </ul>

              <div className="bg-red-100 p-3 rounded mt-3">
                <p className="text-gray-800 font-semibold">
                  ⛔ Cheating or any form of dishonesty will result in immediate disqualification and possible expulsion.
                </p>
              </div>
            </section>

            {/* Refund Policy */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">7. Refund Policy</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-green-50 p-3 rounded">
                  <h4 className="font-bold text-green-700 mb-2">✅ Eligible for Refund</h4>
                  <ul className="list-disc pl-5 text-gray-700 text-sm">
                    <li>Institute cancellation of exam</li>
                    <li>Technical errors resulting in duplicate bookings</li>
                    <li>Overpayment of fees</li>
                  </ul>
                </div>

                <div className="bg-red-50 p-3 rounded">
                  <h4 className="font-bold text-red-700 mb-2">❌ NOT Eligible for Refund</h4>
                  <ul className="list-disc pl-5 text-gray-700 text-sm">
                    <li>Change of mind after booking</li>
                    <li>Failure to attend booked exam (no-show)</li>
                    <li>Late cancellation (within 3 days)</li>
                    <li>Expired credits after validity period</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Amendments */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">8. Amendments and Updates</h3>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 rounded">
                <p className="font-bold text-yellow-800 mb-2">⚡ Right to Modify</p>
                <p className="text-gray-800">
                  <strong>Radiance Education reserves the absolute right to:</strong>
                </p>
                <ul className="list-disc pl-5 text-gray-700 mt-1">
                  <li>Modify any or all exam booking conditions at any time</li>
                  <li>Add new rules, policies, or requirements</li>
                  <li>Change fee structures, credit systems, or validity periods</li>
                </ul>
                <p className="text-gray-800 font-semibold mt-2">
                  Continued use of the booking system after changes indicates acceptance.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">9. Contact Information</h3>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="font-bold text-blue-800">Radiance Education</p>
                <p className="text-gray-700">📧 Email: info@radianceedu.com</p>
                <p className="text-gray-700">📱 Phone: +880 1759787364</p>
                <p className="text-gray-700">🏢 Address: Scout Building-2nd Floor, Opposite to Moulvibazar Pourosova, Court Road, Moulvibazar-3200</p>
                <p className="text-gray-700">🔗 Portal: app.radianceedu.net</p>
                <p className="text-sm text-gray-600 mt-3">
                  <strong>Office Hours:</strong> 10:00 AM - 06:00 PM (Friday closed)
                </p>
                <p className="text-xs text-gray-500 mt-1 italic">
                  *Except any holiday or emergency office closure by Radiance, otherwise always 10 AM - 6 PM
                </p>
              </div>
            </section>

            {/* Final Acknowledgment */}
            <section>
              <h3 className="text-lg font-bold text-red-700 mb-3">10. Final Acknowledgment</h3>
              <div className="bg-red-50 p-4 rounded border-2 border-red-300">
                <p className="text-gray-800 mb-3">By booking an exam through the Radiance Education portal, you confirm that:</p>
                <div className="space-y-1 text-gray-700">
                  <p>✅ You have read and fully understood all exam booking conditions</p>
                  <p>✅ You agree to comply with all rules, policies, and regulations</p>
                  <p>✅ You understand the no-show policy and associated penalties</p>
                  <p>✅ You acknowledge that credits/exams may be deducted for policy violations</p>
                  <p>✅ You accept that the institute can modify these conditions at any time</p>
                </div>
                
                <div className="bg-red-200 p-3 rounded mt-3">
                  <p className="font-bold text-red-900 mb-1">⚠️ Failure to comply may result in:</p>
                  <ul className="list-disc pl-5 text-gray-800 text-sm">
                    <li>Forfeiture of credits</li>
                    <li>Additional penalty deductions</li>
                    <li>Suspension of booking privileges</li>
                    <li>Termination of portal access</li>
                    <li>Cancellation of enrollment</li>
                  </ul>
                </div>
              </div>
            </section>

            <div className="text-center mt-8 pt-4 border-t">
              <p className="text-sm text-gray-600">© 2025-2026 Radiance Education. All rights reserved. Development Credit: <a href="https://virtualswift.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">VirtualSwift</a></p>
              <p className="text-xs text-gray-500 mt-1">All booking services are provided through official Radiance Education platforms under the direct supervision of the institute.</p>
              <p className="text-xs text-gray-500 mt-1"><strong>Version:</strong> 2.1 | <strong>Last Review:</strong> December 30, 2025</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Exam Results Modal */}
    <Dialog open={showResultsModal} onOpenChange={setShowResultsModal}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            Exam Results
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto h-[70vh]">
          <iframe
            src="https://radianceedu.com/exam-results"
            title="Exam Results"
            className="w-full h-full border-0 rounded-lg"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </div>
      </DialogContent>
    </Dialog>

    {/* Privacy Policy Modal */}
    <Dialog open={showPrivacyModal} onOpenChange={setShowPrivacyModal}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-blue-800">
            Privacy Policy
          </DialogTitle>
          <p className="text-sm text-gray-500 text-center">Last Updated: December 30, 2025</p>
        </DialogHeader>
        <div className="overflow-y-auto h-[75vh] px-6 py-4 prose prose-sm max-w-none">
          <div className="space-y-6">
            {/* Introduction */}
            <section>
              <h3 className="text-lg font-bold text-blue-700 mb-2">1. Introduction</h3>
              <p className="text-gray-700">Welcome to Radiance Education's official online platform. This Privacy Policy explains how we collect, use, protect, and share your personal information when you access our services through our official websites and applications.</p>
              
              <h4 className="font-bold text-blue-600 mt-3 mb-1">Official Platforms</h4>
              <p className="text-gray-700 mb-1">This Privacy Policy applies to all official Radiance Education platforms:</p>
              <ul className="list-disc pl-5 text-gray-700">
                <li><strong>radianceedu.com</strong> - Main institutional website</li>
                <li><strong>radianceedu.net (including subdomains like app.radianceedu.net)</strong> - Educational resources, Student portal and exam booking system</li>
                <li><strong>radianceedu.app</strong></li>
              </ul>
              <p className="text-gray-700 mt-2">All these platforms are owned and operated by <strong>Radiance Education</strong> and are under the direct supervision of the institute's management.</p>
            </section>

            {/* Acceptance of Terms */}
            <section>
              <h3 className="text-lg font-bold text-blue-700 mb-2">2. Acceptance of Terms</h3>
              <p className="text-gray-700 mb-2">By registering as a student with Radiance Education and using our online portal, you acknowledge and agree that:</p>
              <ul className="list-disc pl-5 text-gray-700">
                <li>You have read and understood this Privacy Policy</li>
                <li>You accept all terms and conditions mentioned in your admission form</li>
                <li>You are bound by all policies, rules, and regulations set forth by Radiance Education</li>
                <li>You consent to the collection and use of your information as described in this policy</li>
              </ul>
              <p className="text-red-600 font-semibold mt-3">Students who have been admitted to Radiance Education are legally bound to follow all terms, conditions, and policies mentioned at the time of admission and any subsequent updates communicated by the institute.</p>
            </section>

            {/* Information We Collect */}
            <section>
              <h3 className="text-lg font-bold text-blue-700 mb-2">3. Information We Collect</h3>
              <h4 className="font-bold text-blue-600 mt-3 mb-1">3.1 Personal Information</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Full name, email address, phone number</li>
                <li>Date of birth and gender</li>
                <li>National ID/Passport information (for IELTS main exam registration)</li>
                <li>Photographs for identification purposes</li>
                <li>Academic background and qualifications</li>
                <li>Emergency contact information</li>
              </ul>

              <h4 className="font-bold text-blue-600 mt-3 mb-1">3.2 Academic Information</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Course enrollment details</li>
                <li>Exam registration and booking history</li>
                <li>Test scores and performance records</li>
                <li>Module-wise credits and progress tracking</li>
              </ul>
            </section>

            {/* How We Use Your Information */}
            <section>
              <h3 className="text-lg font-bold text-blue-700 mb-2">4. How We Use Your Information</h3>
              <h4 className="font-bold text-blue-600 mt-3 mb-1">4.1 Educational Services</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Managing your student account and enrollment</li>
                <li>Scheduling and conducting exams (Mock Tests, Partial Exams)</li>
                <li>Tracking academic progress and performance</li>
                <li>Issuing certificates and official documents</li>
              </ul>

              <h4 className="font-bold text-blue-600 mt-3 mb-1">4.2 Communication</h4>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Sending important notifications about exams, classes, and deadlines</li>
                <li>Providing updates on schedule changes or cancellations</li>
                <li>Responding to your inquiries and support requests</li>
              </ul>
            </section>

            {/* Data Security */}
            <section>
              <h3 className="text-lg font-bold text-blue-700 mb-2">5. Data Security</h3>
              <p className="text-gray-700 mb-2">Radiance Education takes the security of your information seriously:</p>
              <ul className="list-disc pl-5 text-gray-700">
                <li><strong>Encryption:</strong> All sensitive data is encrypted during transmission and storage</li>
                <li><strong>Access Control:</strong> Only authorized staff members can access student information</li>
                <li><strong>Regular Audits:</strong> We conduct periodic security assessments</li>
                <li><strong>Secure Servers:</strong> Our servers are protected by industry-standard security measures</li>
              </ul>
            </section>

            {/* Portal Validity */}
            <section>
              <h3 className="text-lg font-bold text-blue-700 mb-2">6. Portal Validity and Access</h3>
              <ul className="list-disc pl-5 text-gray-700">
                <li>Your portal access is valid for <strong>5 months from the start of your class</strong></li>
                <li>Mock test and partial exam booking facilities are available during this validity period</li>
                <li>You must complete all required exams within the validity period</li>
                <li>Access may be temporarily suspended for non-payment of fees or violation of policies</li>
              </ul>
            </section>

            {/* Contact Information */}
            <section>
              <h3 className="text-lg font-bold text-blue-700 mb-2">7. Contact Information</h3>
              <p className="text-gray-700 mb-2">For any questions, concerns, or requests regarding this Privacy Policy or your personal information, please contact:</p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="font-bold text-blue-800">Radiance Education</p>
                <p className="text-gray-700">📧 Support: info@radianceedu.com</p>
                <p className="text-gray-700">📱 Phone: +880 1759787364</p>
                <p className="text-gray-700">🏢 Address: Scout Building-2nd Floor, Opposite to Moulvibazar Pourosova, Court Road, Moulvibazar-3200</p>
                <p className="text-gray-700">🔗 Website: www.radianceedu.com</p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section>
              <h3 className="text-lg font-bold text-blue-700 mb-2">8. Acknowledgment</h3>
              <p className="text-gray-700 mb-2">By creating an account and using Radiance Education's portal, you acknowledge that:</p>
              <ul className="list-disc pl-5 text-gray-700">
                <li>You have read and understood this Privacy Policy</li>
                <li>You consent to the collection and use of your information as described</li>
                <li>You are bound by all terms mentioned in your admission form</li>
                <li>You agree to comply with all institute policies and exam booking conditions</li>
              </ul>
            </section>

            <div className="text-center mt-8 pt-4 border-t">
              <p className="text-sm text-gray-600">© 2025-2026 Radiance Education. All rights reserved. Development Credit: <a href="https://virtualswift.com/" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">VirtualSwift</a></p>
              <p className="text-xs text-gray-500 mt-1">This Privacy Policy is governed by the laws of Bangladesh.</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Send Message Modal */}
    <SendMessageModal 
      isOpen={showSendMessageModal}
      onClose={() => setShowSendMessageModal(false)}
    />
    </>
  );
};

export default Footer;
